/* ==========================================================================
   SN THEME — Second Nature homepage behaviour
   --------------------------------------------------------------------------
   One file, four public init functions (plus a small mobile-menu helper):

     initHeaderScroll()   SECTION 3 — toggles .is-scrolled on the header +
                          ticker via an IntersectionObserver on the hero
     initSearchOverlay()  SECTION 4 — opens/closes the search panel and
                          forces the header into State B while open
     initTicker()         SECTION 2 — builds a seamless marquee
     initProductRail()    SECTION 5 — wishlist hearts + prev/next arrows
     initMobileMenu()     sidebar menu (hamburger, all breakpoints)
     initLocaleDrawer()   region + language drawer ("CAD / EN" button)
     initLookbook()       SECTION 8 — look popup with product quote lines

   Everything re-runs safely: boot() is idempotent and is also called on
   Shopify theme-editor section reloads and on resize.
   ========================================================================== */

(function () {
  'use strict';

  if (window.SNTheme && window.SNTheme.booted) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- shared helpers ------------------------------------------------- */

  function scrollTargets() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-sn-scroll-target]'));
  }

  function setStateClass(className, on) {
    scrollTargets().forEach(function (el) {
      el.classList.toggle(className, !!on);
    });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  function headerEl() {
    return document.querySelector('[data-sn-header]');
  }

  /* =====================================================================
     SECTION 3 — Header scroll state
     Observes the hero. When the hero's bottom edge passes the bottom of
     the header stack (ticker + header), .is-scrolled is added to every
     [data-sn-scroll-target]. No scroll listener.
     ===================================================================== */

  var heroObserver = null;

  function initHeaderScroll() {
    var header = headerEl();
    if (!header) return;

    if (heroObserver) {
      heroObserver.disconnect();
      heroObserver = null;
    }

    var hero = document.querySelector('#MainContent > .shopify-section:first-child [data-sn-hero]');

    if (!hero) {
      setStateClass('is-scrolled', true);
      return;
    }

    var headerBottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));

    heroObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          setStateClass('is-scrolled', !entry.isIntersecting);
        });
      },
      {
        root: null,
        rootMargin: '-' + headerBottom + 'px 0px 0px 0px',
        threshold: 0
      }
    );

    heroObserver.observe(hero);
  }

  /* Product pages: the header renders a second wordmark (data-sn-logo-layer)
     whose overlay blend only works outside the fixed header's stacking
     context, so move it to <body>. Stale copies (editor re-renders) go. */
  function initLogoLayer() {
    var stale = document.querySelectorAll('body > .sn-logo-layer');
    for (var i = 0; i < stale.length; i++) {
      stale[i].parentNode.removeChild(stale[i]);
    }
    var header = headerEl();
    var layer = header ? header.querySelector('[data-sn-logo-layer]') : null;
    if (!layer) return;
    document.body.appendChild(layer);
  }

  /* =====================================================================
     SECTION 4 — Search overlay
     ===================================================================== */

  function initSearchOverlay() {
    var header = headerEl();
    if (!header || header.dataset.snSearchBound) return;
    header.dataset.snSearchBound = 'true';

    var panel = header.querySelector('[data-sn-search]');
    if (!panel) return;

    var toggles = header.querySelectorAll('[data-sn-search-toggle]');
    var closers = header.querySelectorAll('[data-sn-search-close]');
    var headerInput = header.querySelector('[data-sn-search-input]');
    var input = panel.querySelector('input[type="search"]');
    var lastFocus = null;

    function isOpen() {
      return panel.classList.contains('is-open');
    }

    /* fromHeaderInput: the desktop search box opened the panel on focus, so
       leave focus where it is instead of moving it into the panel. */
    function open(fromHeaderInput) {
      if (isOpen()) return;
      closeMenu();
      closeLocale();
      lastFocus = document.activeElement;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      setStateClass('search-open', true);
      Array.prototype.forEach.call(toggles, function (t) {
        t.setAttribute('aria-expanded', 'true');
      });
      if (!fromHeaderInput && input && input.offsetParent !== null) {
        window.setTimeout(function () {
          input.focus();
        }, 50);
      }
    }

    if (headerInput) {
      headerInput.addEventListener('focus', function () {
        open(true);
      });
      headerInput.addEventListener('click', function () {
        open(true);
      });
    }

    function close() {
      if (!isOpen()) return;
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      setStateClass('search-open', false);
      Array.prototype.forEach.call(toggles, function (t) {
        t.setAttribute('aria-expanded', 'false');
      });
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    Array.prototype.forEach.call(toggles, function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        isOpen() ? close() : open();
      });
    });

    Array.prototype.forEach.call(closers, function (c) {
      c.addEventListener('click', function (e) {
        e.preventDefault();
        close();
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    document.addEventListener('click', function (e) {
      if (!isOpen()) return;
      if (header.contains(e.target)) return;
      close();
    });

    window.SNTheme.closeSearch = close;
  }

  /* =====================================================================
     Sidebar menu (hamburger, slides in from the left)
     ===================================================================== */

  function closeMenu() {
    if (window.SNTheme && typeof window.SNTheme.closeMenu === 'function') {
      window.SNTheme.closeMenu();
    }
  }

  function initMobileMenu() {
    var header = headerEl();
    if (!header || header.dataset.snMenuBound) return;
    header.dataset.snMenuBound = 'true';

    var panel = header.querySelector('[data-sn-menu]');
    var toggle = header.querySelector('[data-sn-menu-toggle]');
    var closers = header.querySelectorAll('[data-sn-menu-close]');
    if (!panel || !toggle) return;

    /* Second pane (Shop categories): opens on tap/click only (hover was too
       easy to trigger by accident), closes with Back or Escape */
    var subToggles = Array.prototype.slice.call(panel.querySelectorAll('[data-sn-submenu-open]'));
    var subs = Array.prototype.slice.call(panel.querySelectorAll('[data-sn-submenu]'));
    var lastToggle = null;

    function subOpen() {
      return panel.classList.contains('is-sub');
    }

    function openSub(id, viaHover) {
      var target = null;
      subs.forEach(function (s) {
        var on = s.getAttribute('data-sn-submenu') === id;
        s.classList.toggle('is-open', on);
        s.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (on) target = s;
      });
      if (!target) return;
      panel.classList.add('is-sub');
      subToggles.forEach(function (t) {
        var on = t.getAttribute('data-sn-submenu-open') === id;
        t.setAttribute('aria-expanded', on ? 'true' : 'false');
        if (on) lastToggle = t;
      });
      if (!viaHover) {
        var first = target.querySelector('.sn-menu__list a');
        window.setTimeout(function () { if (first && subOpen()) first.focus(); }, 380);
      }
    }

    function closeSub(returnFocus) {
      if (!subOpen()) return;
      panel.classList.remove('is-sub');
      subs.forEach(function (s) {
        s.classList.remove('is-open');
        s.setAttribute('aria-hidden', 'true');
      });
      subToggles.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      if (returnFocus && lastToggle) lastToggle.focus();
    }

    subToggles.forEach(function (t) {
      var id = t.getAttribute('data-sn-submenu-open');
      t.addEventListener('click', function (e) {
        e.preventDefault();
        if (t.getAttribute('aria-expanded') === 'true') closeSub(true);
        else openSub(id, false);
      });
    });

    Array.prototype.forEach.call(panel.querySelectorAll('[data-sn-submenu-back]'), function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        closeSub(true);
      });
    });

    function isOpen() {
      return panel.classList.contains('is-open');
    }

    function open() {
      if (window.SNTheme.closeSearch) window.SNTheme.closeSearch();
      closeLocale();
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      setStateClass('menu-open', true);
    }

    function close() {
      if (!isOpen()) return;
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      setStateClass('menu-open', false);
      closeSub(false);
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      isOpen() ? close() : open();
    });

    Array.prototype.forEach.call(closers, function (c) {
      c.addEventListener('click', function (e) {
        e.preventDefault();
        close();
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || !isOpen()) return;
      if (subOpen()) closeSub(true);
      else close();
    });

    document.addEventListener('click', function (e) {
      if (!isOpen()) return;
      if (header.contains(e.target)) return;
      close();
    });

    window.SNTheme.closeMenu = close;
  }

  /* =====================================================================
     Region + language drawer
     Right-hand drawer over Shopify's localization form. Typing filters the
     country list; clicking a country writes its code into the hidden
     country_code input and submits; changing the language select submits.
     ===================================================================== */

  function closeLocale() {
    if (window.SNTheme && typeof window.SNTheme.closeLocale === 'function') {
      window.SNTheme.closeLocale();
    }
  }

  function initLocaleDrawer() {
    var header = headerEl();
    if (!header || header.dataset.snLocaleBound) return;
    header.dataset.snLocaleBound = 'true';

    var panel = header.querySelector('[data-sn-locale]');
    var toggles = header.querySelectorAll('[data-sn-locale-toggle]');
    if (!panel || !toggles.length) return;

    var closers = panel.querySelectorAll('[data-sn-locale-close]');
    var filter = panel.querySelector('[data-sn-locale-filter]');
    var items = panel.querySelectorAll('[data-sn-locale-country]');
    var empty = panel.querySelector('[data-sn-locale-empty]');
    var countryInput = panel.querySelector('[data-sn-locale-country-input]');
    var langSelect = panel.querySelector('[data-sn-locale-lang]');
    var form = panel.querySelector('form');

    function isOpen() {
      return panel.classList.contains('is-open');
    }

    function setExpanded(on) {
      Array.prototype.forEach.call(toggles, function (t) {
        t.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
    }

    function open() {
      if (window.SNTheme.closeSearch) window.SNTheme.closeSearch();
      closeMenu();
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      setExpanded(true);
      setStateClass('menu-open', true);
      if (filter) {
        window.setTimeout(function () {
          filter.focus();
        }, 60);
      }
    }

    function close() {
      if (!isOpen()) return;
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      setExpanded(false);
      setStateClass('menu-open', false);
    }

    function applyFilter() {
      var q = (filter ? filter.value : '').trim().toLowerCase();
      var shown = 0;
      Array.prototype.forEach.call(items, function (item) {
        var hay = (item.getAttribute('data-sn-locale-name') || '').toLowerCase() + ' ' + (item.getAttribute('data-sn-locale-currency') || '').toLowerCase();
        var hit = !q || hay.indexOf(q) > -1;
        item.parentNode.hidden = !hit;
        if (hit) shown++;
      });
      if (empty) empty.hidden = shown > 0;
    }

    Array.prototype.forEach.call(toggles, function (t) {
      t.addEventListener('click', function (e) {
        e.preventDefault();
        isOpen() ? close() : open();
      });
    });

    Array.prototype.forEach.call(closers, function (c) {
      c.addEventListener('click', function (e) {
        e.preventDefault();
        close();
      });
    });

    if (filter) {
      filter.addEventListener('input', applyFilter);
      filter.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') e.preventDefault();
      });
    }

    Array.prototype.forEach.call(items, function (item) {
      item.addEventListener('click', function (e) {
        e.preventDefault();
        if (countryInput) countryInput.value = item.getAttribute('data-sn-locale-country');
        if (form) form.submit();
      });
    });

    if (langSelect) {
      langSelect.addEventListener('change', function () {
        if (form) form.submit();
      });
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    document.addEventListener('click', function (e) {
      if (!isOpen()) return;
      if (header.contains(e.target)) return;
      close();
    });

    window.SNTheme.closeLocale = close;
  }

  /* =====================================================================
     SECTION 8 — Lookbook popup
     Each look button has a sibling <script type="application/json"
     data-sn-look-data="handle"> with the image and its hotspots. Opening a
     look fills the section's popup: image in the middle, a marker on each
     tagged item, a text tag (name + price) on the chosen side, and an SVG
     line that runs flat under the tag then angles to the garment. Tags sit
     just above their marker and are nudged apart. Under 900px the tags stack below
     the image and the markers are numbered instead.

     Calibration (theme editor, or ?sn_calibrate=1): a readout follows the
     cursor over the image with the x / y percentages; click to copy them.
     ===================================================================== */

  function initLookbook() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-lookbook]'), function (section) {
      if (section.dataset.snLookbookBound) return;
      section.dataset.snLookbookBound = 'true';

      var modal = section.querySelector('[data-sn-lookmodal]');
      if (!modal) return;

      var stage = modal.querySelector('[data-sn-lookmodal-stage]');
      var figure = modal.querySelector('[data-sn-lookmodal-figure]');
      var img = modal.querySelector('[data-sn-lookmodal-img]');
      var glow = modal.querySelector('[data-sn-lookmodal-glow]');
      var starTpl = modal.querySelector('[data-sn-lookstar]');
      var dots = modal.querySelector('[data-sn-lookmodal-dots]');
      var left = modal.querySelector('[data-sn-lookmodal-left]');
      var right = modal.querySelector('[data-sn-lookmodal-right]');
      var lines = modal.querySelector('[data-sn-lookmodal-lines]');
      var list = modal.querySelector('[data-sn-lookmodal-list]');
      var title = modal.querySelector('[data-sn-lookmodal-title]');
      var readout = modal.querySelector('[data-sn-lookmodal-readout]');
      var closers = modal.querySelectorAll('[data-sn-lookmodal-close]');
      var calibrate = section.hasAttribute('data-sn-lookbook-calibrate') || /[?&]sn_calibrate=1/.test(window.location.search);
      var current = null;
      var marquee = null;
      var lastFocus = null;
      var probe = null;

      if (calibrate) modal.setAttribute('data-sn-calibrate', '');

      function esc(str) {
        return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
          return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
        });
      }

      /* Tag = name + price, the whole thing links to the product */
      function tagHtml(h, n) {
        return (
          '<a class="sn-looktag" href="' + esc(h.url) + '" data-sn-looktag="' + n + '">' +
            '<span class="sn-looktag__n">' + (n + 1) + '</span>' +
            '<span class="sn-looktag__title">' + esc(h.title) + '</span>' +
            '<span class="sn-looktag__price">' + esc(h.price) + '</span>' +
          '</a>'
        );
      }

      function render(data) {
        current = data;
        if (data.image) img.src = data.image; else img.hidden = true;
        if (glow) {
          if (data.glow || data.image) glow.src = data.glow || data.image;
          glow.hidden = !(data.glow || data.image);
        }
        img.alt = data.title || '';
        title.textContent = data.title || '';
        dots.innerHTML = '';
        left.innerHTML = '';
        right.innerHTML = '';
        list.innerHTML = '';
        lines.innerHTML = '';

        (data.hotspots || []).forEach(function (h, i) {
          var dot = document.createElement('span');
          dot.className = 'sn-lookdot';
          dot.style.left = h.x + '%';
          dot.style.top = h.y + '%';
          dot.setAttribute('data-sn-lookdot', i);
          dot.innerHTML = '<span class="sn-lookdot__n">' + (i + 1) + '</span>';
          /* Every star starts on a different frame so the markers never beat
             in unison. One 8-frame loop at 3 fps is 2.6667s. */
          dot.style.setProperty('--sn-star-delay', (-Math.random() * 2.6667).toFixed(3) + 's');
          if (starTpl && starTpl.content) dot.appendChild(starTpl.content.cloneNode(true));
          dots.appendChild(dot);

          var side = h.side === 'left' ? left : right;
          side.insertAdjacentHTML('beforeend', tagHtml(h, i));
          list.insertAdjacentHTML('beforeend', tagHtml(h, i));
        });

        if (img.complete) layout();
        else img.addEventListener('load', layout, { once: true });
      }

      /* Desktop: each tag sits a little above its garment point, tags are
         pushed apart so they never overlap, then one thin line per tag runs
         flat under the whole tag and angles down to the garment. */
      function layout() {
        if (!current || getComputedStyle(left).display === 'none') return;

        var stageRect = stage.getBoundingClientRect();
        var imgRect = img.getBoundingClientRect();
        var GAP = 14;
        var LIFT = 56;
        var UNDERLINE = 4;
        var RUNOUT = 16;

        [left, right].forEach(function (side) {
          var tags = Array.prototype.slice.call(side.querySelectorAll('[data-sn-looktag]'));
          var sideRect = side.getBoundingClientRect();
          var placed = tags.map(function (tag) {
            var i = parseInt(tag.getAttribute('data-sn-looktag'), 10);
            var h = current.hotspots[i];
            var tagH = tag.offsetHeight;
            var want = imgRect.top - sideRect.top + (h.y / 100) * imgRect.height - LIFT - tagH;
            return { tag: tag, top: want, h: tagH };
          });

          placed.sort(function (a, b) { return a.top - b.top; });

          var minTop = 0;
          placed.forEach(function (p) {
            if (p.top < minTop) p.top = minTop;
            minTop = p.top + p.h + GAP;
          });

          var maxBottom = sideRect.height - UNDERLINE - 2;
          for (var k = placed.length - 1; k >= 0; k--) {
            var p = placed[k];
            if (p.top + p.h > maxBottom) p.top = maxBottom - p.h;
            maxBottom = p.top - GAP;
          }

          placed.forEach(function (p) {
            p.tag.style.top = Math.max(0, Math.round(p.top)) + 'px';
          });
        });

        lines.setAttribute('viewBox', '0 0 ' + Math.round(stageRect.width) + ' ' + Math.round(stageRect.height));
        var svg = '';
        Array.prototype.forEach.call(modal.querySelectorAll('[data-sn-looktag]'), function (tag) {
          if (!left.contains(tag) && !right.contains(tag)) return;
          var i = parseInt(tag.getAttribute('data-sn-looktag'), 10);
          var h = current.hotspots[i];
          if (!h) return;
          /* Span the full name + price, whatever the column width, plus a
             little run-out past the outer end */
          var c = tag.getBoundingClientRect();
          var l = c.left, r = c.right, b = c.bottom;
          Array.prototype.forEach.call(tag.children, function (child) {
            var cr = child.getBoundingClientRect();
            if (!cr.width) return;
            l = Math.min(l, cr.left);
            r = Math.max(r, cr.right);
            b = Math.max(b, cr.bottom);
          });
          var isLeft = left.contains(tag);
          var x0 = imgRect.left + (h.x / 100) * imgRect.width - stageRect.left;
          var y0 = imgRect.top + (h.y / 100) * imgRect.height - stageRect.top;
          var y1 = b + UNDERLINE - stageRect.top;
          var xOuter = (isLeft ? l - RUNOUT : r + RUNOUT) - stageRect.left;
          var xInner = (isLeft ? r : l) - stageRect.left;
          svg += '<polyline points="' + [xOuter, y1, xInner, y1, x0, y0].map(function (v) { return Math.round(v * 10) / 10; }).join(' ') + '"></polyline>';
        });
        lines.innerHTML = svg;
      }

      function open(key, opener) {
        var node = section.querySelector('[data-sn-look-data="' + key + '"]');
        if (!node) return;
        var data;
        try { data = JSON.parse(node.textContent); } catch (err) { return; }
        lastFocus = opener || document.activeElement;
        render(data);
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('sn-lookmodal-open');
        if (marquee) marquee.hold();
        window.setTimeout(function () {
          var c = modal.querySelector('.sn-lookmodal__close');
          if (c) c.focus();
          layout();
        }, 60);
      }

      function close() {
        if (!modal.classList.contains('is-open')) return;
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('sn-lookmodal-open');
        if (marquee) marquee.release();
        if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      }

      /* Delegated so the marquee clones open the popup too. A pointer-captured
         drag can retarget the click at the track, so fall back to the element
         under the pointer before giving up. */
      section.addEventListener('click', function (e) {
        var btn = e.target && e.target.closest ? e.target.closest('[data-sn-look]') : null;
        if (!btn && (e.clientX || e.clientY)) {
          var under = document.elementFromPoint(e.clientX, e.clientY);
          btn = under && under.closest ? under.closest('[data-sn-look]') : null;
        }
        if (!btn || !section.contains(btn)) return;
        open(btn.getAttribute('data-sn-look'), btn);
      });

      Array.prototype.forEach.call(closers, function (c) {
        c.addEventListener('click', close);
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') close();
      });

      window.addEventListener('resize', debounce(layout, 100));

      /* Rail: scrolls on its own, forever. The set of looks is cloned so the
         strip is several viewports long, and the strip is a real scroll
         container that the script advances by nudging scrollLeft. The
         position lives inside the middle copy of the set; once a full set
         has passed it jumps back one set (the copies are identical, so the
         seam is invisible). Native scrolling paints lazily and reliably no
         matter how long the strip is, which one giant moving transform layer
         does not on large high-density screens. Hovering a look eases the
         speed down and dims the others (CSS); leaving eases it back up.
         Dragging, swiping or wheel-scrolling takes over at the user's speed
         and auto-scroll resumes a moment after they let go. */
      var track = section.querySelector('[data-sn-rail-track]');
      if (track && section.classList.contains('sn-lookbook--rail') && !reduceMotion.matches) {
        var originals = Array.prototype.slice.call(track.querySelectorAll('[data-sn-look]'));
        if (originals.length) {
          var SPEED = 40;
          var SLOW = 8;
          var RESUME_AFTER = 1500;
          var target = SPEED;
          var speed = SPEED;
          var offset = 0;
          var expected = -1;
          var last = null;
          var frames = 0;
          var drag = null;
          var idleTimer = null;
          var suppressClickUntil = 0;
          var held = false;

          function setWidth() {
            return originals[0].offsetWidth * originals.length;
          }

          function viewport() {
            return Math.max(track.clientWidth, section.clientWidth, window.innerWidth || 0);
          }

          /* Everything in the strip loads up front: a lazy image popping in
             mid-scroll is what made the loop look torn. */
          function eager(el) {
            Array.prototype.forEach.call(el.querySelectorAll('img'), function (img) {
              img.loading = 'eager';
              img.decoding = 'async';
            });
          }

          /* Clone until there is a full set before the start position and two
             viewports after it, so the loop can run either way. */
          function fill() {
            Array.prototype.forEach.call(track.querySelectorAll('[data-sn-look-clone]'), function (c) {
              c.parentNode.removeChild(c);
            });
            var w = setWidth();
            if (!w) return;
            var copies = Math.max(2, Math.ceil((viewport() * 2) / w) + 1);
            originals.forEach(eager);
            for (var i = 0; i < copies; i++) {
              originals.forEach(function (o) {
                var c = o.cloneNode(true);
                c.setAttribute('data-sn-look-clone', '');
                c.setAttribute('aria-hidden', 'true');
                c.setAttribute('tabindex', '-1');
                eager(c);
                track.appendChild(c);
              });
            }
          }

          /* Keep the position inside the middle set: [w, 2w) */
          function wrap() {
            var w = setWidth();
            if (!w) return;
            while (offset >= w * 2) offset -= w;
            while (offset < w) offset += w;
          }

          function paint() {
            track.scrollLeft = offset;
            expected = track.scrollLeft;
          }

          function restingSpeed() {
            if (held) return 0;
            return track.classList.contains('is-hovering') ? SLOW : SPEED;
          }

          /* User is steering: auto-scroll stops and waits a moment after they let go */
          function pause() {
            clearTimeout(idleTimer);
            target = 0;
            speed = 0;
          }

          function resumeLater() {
            clearTimeout(idleTimer);
            idleTimer = setTimeout(function () {
              target = restingSpeed();
            }, RESUME_AFTER);
          }

          function tick(now, manual) {
            if (last == null) last = now;
            var dt = Math.min(64, now - last);
            last = now;
            speed += (target - speed) * 0.06;
            if (held) speed = 0;
            if (!drag && speed > 0.01) {
              offset += (speed * dt) / 1000;
              wrap();
              paint();
            }
            if (++frames % 120 === 0) {
              var w = setWidth();
              if (w && track.scrollWidth < w * 2 + viewport() * 2 - 4) {
                fill();
                wrap();
                paint();
              }
            }
            if (!manual) window.requestAnimationFrame(tick);
          }

          /* The popup freezes the strip so the look you opened is still there
             when you close it. */
          marquee = {
            hold: function () { held = true; pause(); },
            release: function () { held = false; resumeLater(); }
          };

          track.classList.add('is-marquee');
          fill();
          offset = setWidth();
          paint();
          window.requestAnimationFrame(tick);
          window.addEventListener('resize', debounce(function () {
            fill();
            wrap();
            paint();
          }, 150));

          /* The user moved it themselves (swipe, trackpad, wheel): follow them */
          track.addEventListener('scroll', function () {
            if (Math.abs(track.scrollLeft - expected) < 2) return;
            pause();
            offset = track.scrollLeft;
            wrap();
            if (Math.abs(track.scrollLeft - offset) > 1) paint();
            expected = track.scrollLeft;
            resumeLater();
          }, { passive: true });

          track.addEventListener('touchstart', function () {
            pause();
          }, { passive: true });

          track.addEventListener('touchend', resumeLater, { passive: true });

          /* Hover: ease down + dim the others (CSS) */
          track.addEventListener('mouseover', function (e) {
            if (!(e.target.closest && e.target.closest('[data-sn-look]'))) return;
            track.classList.add('is-hovering');
            if (target !== 0) target = SLOW;
          });

          track.addEventListener('mouseout', function (e) {
            var to = e.relatedTarget;
            if (to && to.closest && track.contains(to) && to.closest('[data-sn-look]')) return;
            track.classList.remove('is-hovering');
            if (target !== 0) target = SPEED;
          });

          /* Mouse drag: the strip follows the pointer (touch scrolls natively) */
          track.addEventListener('pointerdown', function (e) {
            if (e.button || e.pointerType !== 'mouse') return;
            drag = { id: e.pointerId, x: e.clientX, start: track.scrollLeft, moved: false, captured: false };
            pause();
          });

          track.addEventListener('pointermove', function (e) {
            if (!drag) return;
            var dx = e.clientX - drag.x;
            if (Math.abs(dx) > 4 && !drag.moved) {
              drag.moved = true;
              /* Capture only once a real drag starts. Capturing on pointerdown
                 retargets the click to the track, which swallowed every click
                 on a look. */
              drag.captured = true;
              try { track.setPointerCapture(drag.id); } catch (err) {}
            }
            offset = drag.start - dx;
            var before = offset;
            wrap();
            if (offset !== before) drag.start += offset - before;
            paint();
          });

          function endDrag() {
            if (!drag) return;
            if (drag.moved) suppressClickUntil = Date.now() + 300;
            if (drag.captured) {
              try { track.releasePointerCapture(drag.id); } catch (err) {}
            }
            drag = null;
            resumeLater();
          }

          track.addEventListener('pointerup', endDrag);
          track.addEventListener('pointercancel', endDrag);
          track.addEventListener('dragstart', function (e) {
            e.preventDefault();
          });

          /* A drag must not count as a click on the look underneath */
          section.addEventListener('click', function (e) {
            if (Date.now() < suppressClickUntil) {
              e.stopPropagation();
              e.preventDefault();
            }
          }, true);

          /* Test hook: step the loop by hand where animation frames do not run */
          track.snMarquee = {
            step: function (ms) {
              tick((last == null ? 0 : last) + (ms || 16), true);
            },
            state: function () {
              return { offset: offset, speed: speed, target: target, setWidth: setWidth(), scrollLeft: track.scrollLeft };
            }
          };
        }
      }

      /* Calibration readout */
      if (calibrate && readout) {
        readout.hidden = false;
        readout.textContent = 'Move over the image · click to copy x, y';

        function pct(e) {
          var r = img.getBoundingClientRect();
          var x = Math.min(100, Math.max(0, ((e.clientX - r.left) / r.width) * 100));
          var y = Math.min(100, Math.max(0, ((e.clientY - r.top) / r.height) * 100));
          return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 };
        }

        img.addEventListener('mousemove', function (e) {
          var p = pct(e);
          readout.textContent = 'x ' + p.x + '  ·  y ' + p.y;
        });

        img.addEventListener('click', function (e) {
          var p = pct(e);
          if (!probe) {
            probe = document.createElement('span');
            probe.className = 'sn-lookmodal__probe';
            dots.appendChild(probe);
          }
          probe.style.left = p.x + '%';
          probe.style.top = p.y + '%';
          readout.textContent = 'Copied: x ' + p.x + '  ·  y ' + p.y;
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(p.x + ', ' + p.y).catch(function () {});
          }
        });
      }
    });
  }

  /* =====================================================================
     SECTION 2 — Announcement ticker
     The Liquid renders ONE "set" of items. We clone it until the track is
     at least one set wider than the viewport, then animate by exactly one
     set width so the loop is seamless. Speed is px/second from settings.
     ===================================================================== */

  function initTicker() {
    var tickers = document.querySelectorAll('[data-sn-ticker]');

    Array.prototype.forEach.call(tickers, function (ticker) {
      var track = ticker.querySelector('[data-sn-ticker-track]');
      var set = ticker.querySelector('[data-sn-ticker-set]');
      if (!track || !set) return;

      Array.prototype.forEach.call(track.querySelectorAll('[data-sn-clone]'), function (c) {
        c.parentNode.removeChild(c);
      });

      if (reduceMotion.matches) {
        ticker.classList.add('is-static');
        return;
      }
      ticker.classList.remove('is-static');

      var setWidth = set.getBoundingClientRect().width;
      var viewWidth = ticker.clientWidth;
      if (!setWidth || !viewWidth) return;

      var copies = Math.ceil(viewWidth / setWidth) + 1;
      for (var i = 0; i < copies; i++) {
        var clone = set.cloneNode(true);
        clone.setAttribute('data-sn-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        track.appendChild(clone);
      }

      var speed = parseFloat(ticker.getAttribute('data-sn-speed')) || 60;
      track.style.setProperty('--sn-ticker-shift', (-setWidth) + 'px');
      track.style.setProperty('--sn-ticker-duration', (setWidth / speed).toFixed(2) + 's');
    });
  }

  /* The newsletter placeholder rotates through the addresses in the footer
     setting, one picked per page load. Shopify caches the rendered markup, so
     the shuffle has to happen here rather than in Liquid. */
  function initNewsletter() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-placeholders]'), function (input) {
      var list;
      try {
        list = JSON.parse(input.getAttribute('data-sn-placeholders'));
      } catch (err) {
        return;
      }
      if (!list || list.length < 2) return;
      input.placeholder = list[Math.floor(Math.random() * list.length)];
    });
  }

  /* =====================================================================
     SECTION 5 — Collection rail
     Wishlist hearts persist to localStorage. Prev/next arrows scroll the
     track by one card. Hover image swap is pure CSS.
     ===================================================================== */

  var WISHLIST_KEY = 'sn-wishlist';

  function readWishlist() {
    try {
      var raw = window.localStorage.getItem(WISHLIST_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (err) {
      /* storage unavailable: hearts still toggle visually for this page */
    }
  }

  var WISH_HANDLES_KEY = 'sn-wishlist-handles';

  function readWishHandleMap() {
    try {
      var raw = window.localStorage.getItem(WISH_HANDLES_KEY);
      var parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (err) {
      return {};
    }
  }

  function rememberWishHandle(id, handle, on) {
    if (!id) return;
    var map = readWishHandleMap();
    if (on && handle) map[id] = handle;
    else delete map[id];
    try { window.localStorage.setItem(WISH_HANDLES_KEY, JSON.stringify(map)); } catch (err) {}
  }

  function readWishlistHandles() {
    var map = readWishHandleMap();
    return readWishlist().slice().reverse().map(function (id) { return map[id]; }).filter(Boolean);
  }

  function initProductRail() {
    var wishlist = readWishlist();

    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-wish]'), function (btn) {
      var id = btn.getAttribute('data-sn-wish');
      btn.setAttribute('aria-pressed', wishlist.indexOf(id) > -1 ? 'true' : 'false');

      if (btn.dataset.snBound) return;
      btn.dataset.snBound = 'true';

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var list = readWishlist();
        var idx = list.indexOf(id);
        if (idx > -1) {
          list.splice(idx, 1);
          btn.setAttribute('aria-pressed', 'false');
          rememberWishHandle(id, null, false);
        } else {
          list.push(id);
          btn.setAttribute('aria-pressed', 'true');
          rememberWishHandle(id, btn.getAttribute('data-sn-wish-handle'), true);
        }
        writeWishlist(list);
      });
    });

    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-rail]'), function (rail) {
      if (rail.dataset.snBound) return;
      rail.dataset.snBound = 'true';

      var track = rail.querySelector('[data-sn-rail-track]');
      var prev = rail.querySelector('[data-sn-rail-prev]');
      var next = rail.querySelector('[data-sn-rail-next]');
      if (!track) return;

      function cardStep() {
        var card = track.querySelector('.sn-card');
        if (!card) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      function updateArrows() {
        if (!prev || !next) return;
        var max = track.scrollWidth - track.clientWidth - 1;
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft >= max;
      }

      if (prev) {
        prev.addEventListener('click', function () {
          track.scrollBy({ left: -cardStep(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          track.scrollBy({ left: cardStep(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        });
      }

      track.addEventListener('scroll', debounce(updateArrows, 60), { passive: true });
      updateArrows();
    });
  }

  /* =====================================================================
     SECTION 9 — Product page
     Gallery: the slides sit in one flex track moved with a transform. The
     first/last couple of slides are cloned onto the opposite ends so the
     strip loops without a visible jump: after a transition lands on a
     clone, the track snaps (no animation) to the real slide. Drag / swipe,
     arrows, thumbnails and arrow keys all go through goTo().
     Variants: every option button (info column and mobile sheet alike)
     writes into `selected`, the matching variant drives the hidden id,
     price, availability, URL and the gallery image.
     Add to cart posts to /cart/add.js and bumps the header count.
     ===================================================================== */

  var RECENT_KEY = 'sn-recent';

  function readJson(node) {
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (err) { return null; }
  }

  function readRecent() {
    try {
      var raw = window.localStorage.getItem(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (err) {
      return [];
    }
  }

  function rememberRecent(handle) {
    if (!handle) return;
    var list = readRecent().filter(function (h) { return h !== handle; });
    list.unshift(handle);
    try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12))); } catch (err) {}
  }

  /* Shopify money_format ("${{amount}}", "{{amount_with_comma_separator}} €" ...) */
  function formatMoney(cents, format) {
    var value = (parseInt(cents, 10) || 0) / 100;
    var out = format || '${{amount}}';
    function fixed(n, d, thousands, decimal) {
      var parts = n.toFixed(d).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? parts[0] + decimal + parts[1] : parts[0];
    }
    return out
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, fixed(value, 0, '.', ','))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, fixed(value, 2, '.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, fixed(value, 0, ' ', ','))
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, fixed(value, 2, ' ', ','))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, fixed(value, 2, "'", '.'))
      .replace(/\{\{\s*amount_with_period_and_space_separator\s*\}\}/g, fixed(value, 2, ' ', '.'))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, fixed(value, 0, ',', '.'))
      .replace(/\{\{\s*amount\s*\}\}/g, fixed(value, 2, ',', '.'));
  }

  /* Leave the server-rendered price alone if the format has a token we do not know */
  function writeMoney(el, cents, format) {
    if (!el) return;
    var text = formatMoney(cents, format);
    if (/\{\{/.test(text)) return;
    el.textContent = text;
  }

  function setCartCount(n) {
    var link = document.querySelector('.sn-header__action[href$="/cart"]') || document.querySelector('.sn-header__action[href*="/cart"]');
    if (!link) return;
    var count = link.querySelector('.sn-header__count');
    if (!count) {
      count = document.createElement('span');
      count.className = 'sn-header__count';
      link.appendChild(count);
    }
    count.textContent = n > 0 ? n : '';
    count.setAttribute('aria-label', n + ' items');
  }

  function bumpCartCount(by) {
    var link = document.querySelector('.sn-header__action[href$="/cart"]') || document.querySelector('.sn-header__action[href*="/cart"]');
    if (!link) return;
    var count = link.querySelector('.sn-header__count');
    if (!count) {
      count = document.createElement('span');
      count.className = 'sn-header__count';
      link.appendChild(count);
    }
    var n = (parseInt(count.textContent, 10) || 0) + by;
    count.textContent = n;
    count.setAttribute('aria-label', n + ' items');
  }

  function initProductPage() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-product]'), function (root) {
      if (root.dataset.snBound) return;
      root.dataset.snBound = 'true';

      var config = readJson(root.querySelector('[data-sn-product-config]')) || {};
      var labels = config.labels || {};
      var variants = readJson(root.querySelector('[data-sn-variants]')) || [];
      var handle = root.getAttribute('data-sn-product-handle');
      var productUrl = root.getAttribute('data-sn-product-url') || '';
      var addUrl = root.getAttribute('data-sn-cart-add-url') || '/cart/add.js';
      var sizeIndex = parseInt(root.getAttribute('data-sn-size-index'), 10);
      if (isNaN(sizeIndex)) sizeIndex = -1;
      var desktop = window.matchMedia('(min-width: 1024px)');

      rememberRecent(handle);

      /* ---- Notify me --------------------------------------------------- */
      var notify = root.querySelector('[data-sn-notify]');
      if (notify) {
        var notifyForm = notify.querySelector('[data-sn-notify-form]');
        var notifyStatus = notify.querySelector('[data-sn-notify-status]');
        var notifyReturn = null;

        var openNotify = function () {
          notifyReturn = document.activeElement;
          notify.classList.add('is-open');
          notify.setAttribute('aria-hidden', 'false');
          document.documentElement.classList.add('sn-sheet-open');
          window.setTimeout(function () {
            var field = notify.querySelector('input[type="email"]');
            if (field) field.focus();
          }, 60);
        };

        var closeNotify = function () {
          if (!notify.classList.contains('is-open')) return;
          notify.classList.remove('is-open');
          notify.setAttribute('aria-hidden', 'true');
          document.documentElement.classList.remove('sn-sheet-open');
          if (notifyReturn && notifyReturn.focus) notifyReturn.focus();
        };

        Array.prototype.forEach.call(root.querySelectorAll('[data-sn-notify-open]'), function (b) {
          b.addEventListener('click', openNotify);
        });
        Array.prototype.forEach.call(notify.querySelectorAll('[data-sn-notify-close]'), function (b) {
          b.addEventListener('click', closeNotify);
        });
        document.addEventListener('keydown', function (e) {
          if (e.key === 'Escape' || e.key === 'Esc') closeNotify();
        });

        /* Posted in the background so the page (and the chosen size) stays
           put. Without fetch the form just submits itself. */
        if (notifyForm && window.fetch && window.FormData) {
          notifyForm.addEventListener('submit', function (e) {
            e.preventDefault();
            notifyForm.classList.add('is-busy');
            if (notifyStatus) notifyStatus.textContent = '';
            window.fetch(notifyForm.getAttribute('action') || '/contact', {
              method: 'POST',
              body: new FormData(notifyForm),
              headers: { Accept: 'text/html' }
            }).then(function (res) {
              notifyForm.classList.remove('is-busy');
              if (notifyStatus) notifyStatus.textContent = res.ok ? (labels.notifyDone || '') : (labels.notifyFail || '');
              if (res.ok) notifyForm.reset();
            })['catch'](function () {
              notifyForm.classList.remove('is-busy');
              if (notifyStatus) notifyStatus.textContent = labels.notifyFail || '';
            });
          });
        }
      }

      /* ---- Gallery ---------------------------------------------------- */
      var stage = root.querySelector('[data-sn-gallery-stage]');
      var track = root.querySelector('[data-sn-gallery-track]');
      var slides = track ? Array.prototype.slice.call(track.querySelectorAll('[data-sn-slide]')) : [];
      var n = slides.length;
      var C = n > 1 ? Math.min(2, n) : 0;
      var all = slides.slice();
      var pos = C;
      var cur = 0;
      var status = root.querySelector('[data-sn-gallery-status]');
      var thumbsInner = root.querySelector('[data-sn-gallery-thumbs-inner]');
      var info = root.querySelector('[data-sn-info]');
      var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-sn-thumb]'));
      var dots = Array.prototype.slice.call(root.querySelectorAll('[data-sn-dot]'));
      var settleTimer = null;

      function buildClones() {
        if (n < 2) return;
        Array.prototype.forEach.call(track.querySelectorAll('[data-sn-slide-clone]'), function (c) {
          c.parentNode.removeChild(c);
        });
        for (var i = 0; i < C; i++) {
          var head = slides[n - C + i].cloneNode(true);
          var tail = slides[i].cloneNode(true);
          [head, tail].forEach(function (c) {
            c.setAttribute('data-sn-slide-clone', '');
            c.setAttribute('aria-hidden', 'true');
            c.classList.remove('is-current');
          });
          track.insertBefore(head, slides[0]);
          track.appendChild(tail);
        }
        all = Array.prototype.slice.call(track.querySelectorAll('[data-sn-slide]'));
      }

      /* Track offset that centres slide p in the stage. Slides can differ in
         width, so it is read from the slide itself. On desktop a photo that
         would run under the island is nudged left just enough to clear it. */
      function offsetFor(p) {
        var el = all[p];
        if (!el) return 0;
        var w = el.offsetWidth;
        var left = (stage.clientWidth - w) / 2;
        if (desktop.matches && info && getComputedStyle(info).position === 'absolute') {
          var limit = info.getBoundingClientRect().left - stage.getBoundingClientRect().left - 12;
          if (left + w > limit) left = Math.max(12, limit - w);
        }
        return el.offsetLeft - left;
      }

      function paint(animate, extra) {
        if (!track) return;
        track.style.transition = animate ? '' : 'none';
        track.style.transform = 'translate3d(' + (-offsetFor(pos) + (extra || 0)).toFixed(2) + 'px, 0, 0)';
        if (!animate) {
          void track.offsetWidth;
          track.style.transition = '';
        }
      }

      function markCurrent() {
        cur = n ? (((pos - C) % n) + n) % n : 0;
        all.forEach(function (el, i) {
          el.classList.toggle('is-current', i === pos);
        });
        thumbs.forEach(function (t, i) {
          var on = i === cur;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        dots.forEach(function (d, i) {
          d.classList.toggle('is-active', i === cur);
        });
        if (status) status.textContent = (cur + 1) + ' / ' + n;
        if (thumbsInner && thumbs[cur]) {
          var t = thumbs[cur];
          var left = t.offsetLeft - (thumbsInner.clientWidth - t.offsetWidth) / 2;
          thumbsInner.scrollTo({ left: Math.max(0, left), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        }
      }

      /* After a move that landed on a clone, jump to the real slide silently */
      function settle() {
        if (n < 2) return;
        if (pos >= n + C || pos < C) {
          pos = C + (((pos - C) % n) + n) % n;
          all.forEach(function (el) { el.style.transition = 'none'; });
          all.forEach(function (el, i) { el.classList.toggle('is-current', i === pos); });
          paint(false);
          all.forEach(function (el) { el.style.transition = ''; });
        }
      }

      function goTo(p, animate) {
        if (n < 2) return;
        pos = p;
        paint(animate !== false);
        markCurrent();
        clearTimeout(settleTimer);
        settleTimer = setTimeout(settle, 450);
      }

      /* Relative moves settle first so pos never runs past the clones */
      function stepBy(d) {
        if (n < 2) return;
        clearTimeout(settleTimer);
        settle();
        goTo(pos + d);
      }

      function goToReal(index, animate) {
        if (n < 2) return;
        goTo(((index % n) + n) % n + C, animate);
      }

      if (track && stage) {
        buildClones();
        paint(false);
        markCurrent();

        track.addEventListener('transitionend', function (e) {
          if (e.target === track) settle();
        });

        var prevBtn = root.querySelector('[data-sn-gallery-prev]');
        var nextBtn = root.querySelector('[data-sn-gallery-next]');
        if (prevBtn) prevBtn.addEventListener('click', function () { stepBy(-1); });
        if (nextBtn) nextBtn.addEventListener('click', function () { stepBy(1); });

        thumbs.concat(dots).forEach(function (t) {
          t.addEventListener('click', function () {
            goToReal(parseInt(t.getAttribute('data-sn-thumb') || t.getAttribute('data-sn-dot'), 10));
          });
        });

        stage.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight') { e.preventDefault(); stepBy(1); }
          else if (e.key === 'ArrowLeft') { e.preventDefault(); stepBy(-1); }
          else if (e.key === 'Home') { e.preventDefault(); goToReal(0); }
          else if (e.key === 'End') { e.preventDefault(); goToReal(n - 1); }
        });

        /* Drag / swipe. Nothing is captured until the pointer has actually
           moved, so clicks on the chevrons, thumbnails and dots still land */
        var drag = null;
        var suppressClickUntil = 0;
        stage.addEventListener('pointerdown', function (e) {
          if (n < 2 || e.button) return;
          if (e.target.closest && e.target.closest('button, a, [data-sn-gallery-thumbs-inner]')) return;
          drag = { id: e.pointerId, x: e.clientX, dx: 0, moved: false };
        });
        function onMove(e) {
          if (!drag || e.pointerId !== drag.id) return;
          drag.dx = e.clientX - drag.x;
          if (!drag.moved) {
            if (Math.abs(drag.dx) < 6) return;
            drag.moved = true;
            try { stage.setPointerCapture(drag.id); } catch (err) {}
            track.style.transition = 'none';
          }
          track.style.transform = 'translate3d(' + (-offsetFor(pos) + drag.dx).toFixed(2) + 'px, 0, 0)';
        }
        function endDrag(e) {
          if (!drag || (e && e.pointerId !== drag.id)) return;
          var dx = drag.dx;
          var moved = drag.moved;
          drag = null;
          if (!moved) return;
          suppressClickUntil = Date.now() + 300;
          track.style.transition = '';
          if (dx < -40) stepBy(1);
          else if (dx > 40) stepBy(-1);
          else paint(true);
        }
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', endDrag);
        window.addEventListener('pointercancel', endDrag);

        /* Touch only: hold a photo to magnify it around the finger, lift to
           drop it back. The hold has to out-wait a swipe, so a drag that has
           already started wins and the zoom never fires. */
        var zoomImg = null;
        var zoomTimer = null;
        var zoomFrom = null;

        function zoomOrigin(el, x, y) {
          var r = el.getBoundingClientRect();
          if (!r.width || !r.height) return;
          var px = Math.min(100, Math.max(0, ((x - r.left) / r.width) * 100));
          var py = Math.min(100, Math.max(0, ((y - r.top) / r.height) * 100));
          el.style.transformOrigin = px + '% ' + py + '%';
        }

        function endZoom() {
          window.clearTimeout(zoomTimer);
          zoomTimer = null;
          zoomFrom = null;
          if (!zoomImg) return;
          zoomImg.classList.remove('is-zooming');
          zoomImg.style.transformOrigin = '';
          zoomImg = null;
          stage.classList.remove('is-zoomed');
        }

        stage.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'mouse') return;
          var img = e.target.closest ? e.target.closest('.sn-product__img') : null;
          if (!img) return;
          zoomFrom = { x: e.clientX, y: e.clientY, img: img };
          zoomTimer = window.setTimeout(function () {
            if (!zoomFrom) return;
            zoomImg = zoomFrom.img;
            zoomOrigin(zoomImg, zoomFrom.x, zoomFrom.y);
            zoomImg.classList.add('is-zooming');
            stage.classList.add('is-zoomed');
            drag = null;
          }, 220);
        });

        stage.addEventListener('pointermove', function (e) {
          if (zoomImg) {
            zoomOrigin(zoomImg, e.clientX, e.clientY);
            e.preventDefault();
            return;
          }
          if (zoomFrom && (Math.abs(e.clientX - zoomFrom.x) > 8 || Math.abs(e.clientY - zoomFrom.y) > 8)) {
            window.clearTimeout(zoomTimer);
            zoomFrom = null;
          }
        }, { passive: false });

        stage.addEventListener('pointerup', endZoom);
        stage.addEventListener('pointercancel', endZoom);
        stage.addEventListener('pointerleave', endZoom);
        stage.addEventListener('click', function (e) {
          if (Date.now() < suppressClickUntil) { e.preventDefault(); e.stopPropagation(); }
        }, true);
        stage.addEventListener('dragstart', function (e) { e.preventDefault(); });

        window.addEventListener('resize', debounce(function () { paint(false); }, 100));
        window.addEventListener('load', function () { paint(false); });
      }

      function showMediaFor(variant, animate) {
        if (!variant || n < 2) return;
        var mediaId = variant.featured_media && variant.featured_media.id;
        var imageId = variant.featured_image && variant.featured_image.id;
        var target = -1;
        slides.forEach(function (s, i) {
          if (target > -1) return;
          if (mediaId && String(s.getAttribute('data-sn-media-id')) === String(mediaId)) target = i;
          else if (imageId && String(s.getAttribute('data-sn-image-id')) === String(imageId)) target = i;
        });
        if (target > -1 && target !== cur) goToReal(target, animate);
      }

      /* ---- Variants --------------------------------------------------- */
      var optBtns = Array.prototype.slice.call(root.querySelectorAll('[data-sn-opt-btn]'));
      var optCount = 0;
      optBtns.forEach(function (b) {
        optCount = Math.max(optCount, parseInt(b.getAttribute('data-sn-opt-btn'), 10) + 1);
      });
      var selected = [];
      for (var oi = 0; oi < optCount; oi++) {
        var active = root.querySelector('[data-sn-product-form] [data-sn-opt-btn="' + oi + '"].is-active');
        selected[oi] = active ? active.getAttribute('data-sn-val') : null;
      }

      var idInput = root.querySelector('[data-sn-variant-id]');
      var qtyInput = root.querySelector('[data-sn-qty-input]');
      var priceNow = root.querySelector('[data-sn-price-current]');
      var priceWas = root.querySelector('[data-sn-price-compare]');
      var atcButtons = Array.prototype.slice.call(root.querySelectorAll('[data-sn-atc], [data-sn-atc-bar]'));
      var priceSpans = Array.prototype.slice.call(root.querySelectorAll('[data-sn-atc-price]'));
      var note = root.querySelector('[data-sn-atc-note]');
      var sheet = root.querySelector('[data-sn-sheet]');

      function optionsOf(v) {
        return v.options || [v.option1, v.option2, v.option3].slice(0, optCount);
      }

      function matches(v, sel) {
        var opts = optionsOf(v);
        for (var i = 0; i < optCount; i++) {
          if (sel[i] == null) return false;
          if (opts[i] !== sel[i]) return false;
        }
        return true;
      }

      function findVariant(sel) {
        for (var i = 0; i < variants.length; i++) {
          if (matches(variants[i], sel)) return variants[i];
        }
        return null;
      }

      function firstUnselected() {
        for (var i = 0; i < optCount; i++) if (selected[i] == null) return i;
        return -1;
      }

      function allSelected() {
        return firstUnselected() === -1;
      }

      /* The price shown on the buttons: the chosen variant, else the closest
         match to what has been picked so far, else the first variant */
      function priceVariant() {
        var v = optCount ? findVariant(selected) : variants[0];
        if (v) return v;
        for (var k = 0; k < variants.length; k++) {
          var opts = optionsOf(variants[k]);
          var ok = true;
          for (var j = 0; j < optCount; j++) {
            if (selected[j] != null && opts[j] !== selected[j]) { ok = false; break; }
          }
          if (ok) return variants[k];
        }
        return variants[0] || null;
      }

      function setAtc(state, text) {
        root.classList.toggle('is-soldout', state === 'soldout');
        atcButtons.forEach(function (b) {
          var label = b.querySelector('[data-sn-atc-label]');
          if (label) label.textContent = text;
          b.disabled = state === 'soldout' || state === 'none';
          b.classList.toggle('is-pick', state === 'pick');
        });
        var pv = priceVariant();
        if (pv) {
          priceSpans.forEach(function (el) {
            writeMoney(el, pv.price, config.moneyFormat);
          });
        }
      }

      function updateVariant(fromInit) {
        var v = optCount ? findVariant(selected) : variants[0];

        /* Style every value button for the combination it would produce */
        optBtns.forEach(function (b) {
          var i = parseInt(b.getAttribute('data-sn-opt-btn'), 10);
          var val = b.getAttribute('data-sn-val');
          var trial = selected.slice();
          trial[i] = val;
          var exists = false;
          var available = false;
          for (var k = 0; k < variants.length; k++) {
            var opts = optionsOf(variants[k]);
            var ok = true;
            for (var j = 0; j < optCount; j++) {
              if (j === i) { if (opts[j] !== val) { ok = false; break; } }
              else if (trial[j] != null && opts[j] !== trial[j]) { ok = false; break; }
            }
            if (ok) { exists = true; if (variants[k].available) { available = true; break; } }
          }
          b.classList.toggle('is-active', selected[i] === val);
          b.setAttribute('aria-pressed', selected[i] === val ? 'true' : 'false');
          b.classList.toggle('is-unavailable', !exists);
          b.setAttribute('aria-disabled', exists ? 'false' : 'true');
          b.classList.toggle('is-soldout', exists && !available);
        });

        for (var i = 0; i < optCount; i++) {
          Array.prototype.forEach.call(root.querySelectorAll('[data-sn-opt-value="' + i + '"]'), function (el) {
            el.textContent = selected[i] == null ? '' : selected[i];
          });
          Array.prototype.forEach.call(root.querySelectorAll('[data-sn-bar-value="' + i + '"]'), function (el) {
            el.textContent = selected[i] == null ? (labels.pickShort || 'Select') : selected[i];
          });
        }

        if (v) {
          if (idInput) idInput.value = v.id;
          writeMoney(priceNow, v.price, config.moneyFormat);
          if (priceWas) {
            var onSale = v.compare_at_price && v.compare_at_price > v.price;
            priceWas.hidden = !onSale;
            if (onSale) writeMoney(priceWas, v.compare_at_price, config.moneyFormat);
          }
          setAtc(v.available ? 'ok' : 'soldout', v.available ? (labels.atc || 'Add to cart') : (labels.soldOut || 'Sold out'));
          if (!fromInit) {
            showMediaFor(v);
            if (window.history && window.history.replaceState && productUrl) {
              window.history.replaceState({}, '', productUrl.split('?')[0] + '?variant=' + v.id);
            }
          }
        } else if (!allSelected()) {
          setAtc('pick', labels.pick || 'Select size');
          if (idInput) idInput.value = '';
          if (!fromInit) showMediaFor(priceVariant());
        } else {
          setAtc('none', labels.unavailable || 'Unavailable');
          if (idInput) idInput.value = '';
        }
      }

      optBtns.forEach(function (b) {
        b.addEventListener('click', function () {
          var i = parseInt(b.getAttribute('data-sn-opt-btn'), 10);
          selected[i] = b.getAttribute('data-sn-val');
          updateVariant(false);
          if (sheet && sheet.classList.contains('is-open') && allSelected() && b.closest('[data-sn-sheet]')) {
            window.setTimeout(closeSheet, 220);
          }
        });
      });

      /* Ask for the missing option: the sheet on mobile, a nudge on desktop */
      function askFor(index) {
        if (index < 0) return;
        if (sheet && !desktop.matches) {
          openSheet(index);
          return;
        }
        var group = root.querySelector('[data-sn-product-form] [data-sn-opt="' + index + '"]');
        if (!group) return;
        group.classList.remove('is-attention');
        void group.offsetWidth;
        group.classList.add('is-attention');
        if (group.scrollIntoView) group.scrollIntoView({ block: 'nearest', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        window.setTimeout(function () { group.classList.remove('is-attention'); }, 900);
      }

      /* ---- Quantity --------------------------------------------------- */
      function qty() {
        var q = qtyInput ? parseInt(qtyInput.value, 10) : 1;
        return q > 0 ? q : 1;
      }
      var dec = root.querySelector('[data-sn-qty-dec]');
      var inc = root.querySelector('[data-sn-qty-inc]');
      if (dec) dec.addEventListener('click', function () { if (qtyInput) qtyInput.value = Math.max(1, qty() - 1); });
      if (inc) inc.addEventListener('click', function () { if (qtyInput) qtyInput.value = qty() + 1; });
      if (qtyInput && qtyInput.type === 'number') {
        qtyInput.addEventListener('change', function () { qtyInput.value = qty(); });
      }

      /* ---- Add to cart ------------------------------------------------ */
      var busy = false;
      function addToCart() {
        if (busy) return;
        var v = optCount ? findVariant(selected) : variants[0];
        if (!v) {
          askFor(firstUnselected());
          return;
        }
        if (!v.available) return;
        var quantity = qty();
        busy = true;
        atcButtons.forEach(function (b) { b.classList.add('is-busy'); });
        if (note) note.textContent = '';
        fetch(addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: v.id, quantity: quantity })
        })
          .then(function (r) {
            if (!r.ok) throw new Error('add failed');
            return r.json();
          })
          .then(function () {
            bumpCartCount(quantity);
            setAtc('ok', labels.added || 'Added');
            window.setTimeout(function () { updateVariant(true); }, 1800);
            document.dispatchEvent(new CustomEvent('sn:cart:added', { detail: { variantId: v.id, quantity: quantity } }));
          })
          .catch(function () {
            if (note) note.textContent = labels.error || 'Could not add to cart';
          })
          .then(function () {
            busy = false;
            atcButtons.forEach(function (b) { b.classList.remove('is-busy'); });
          });
      }

      var form = root.querySelector('[data-sn-product-form]');
      if (form) {
        form.addEventListener('submit', function (e) {
          e.preventDefault();
          addToCart();
        });
      }
      Array.prototype.forEach.call(root.querySelectorAll('[data-sn-atc-bar]'), function (b) {
        b.addEventListener('click', addToCart);
      });

      /* ---- Mobile overlay bar ----------------------------------------- */
      var bar = root.querySelector('[data-sn-bar]');
      var mainAtc = root.querySelector('[data-sn-product-form] [data-sn-atc]');

      function setBar(show) {
        if (!bar) return;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if ('inert' in bar) bar.inert = !show;
        root.style.setProperty('--sn-bar-h', show ? bar.offsetHeight + 'px' : '0px');
      }

      var footer = document.querySelector('.sn-section-footer') || document.querySelector('.sn-footer') || document.querySelector('footer');
      var atcOff = false;
      var footerOn = false;
      function syncBar() { setBar(atcOff && !footerOn); }

      if (bar && mainAtc) {
        var r = mainAtc.getBoundingClientRect();
        atcOff = r.bottom < 0 || r.top > window.innerHeight;
        if (footer) {
          var fr = footer.getBoundingClientRect();
          footerOn = fr.top < window.innerHeight && fr.bottom > 0;
        }
        syncBar();
        if ('IntersectionObserver' in window) {
          /* Visible whenever the in-page button is off screen: over the hero
             image at the top, and again once the shopper has scrolled past it.
             Steps aside as soon as the footer comes into view. */
          new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) { atcOff = !entry.isIntersecting; });
            syncBar();
          }, { threshold: 0 }).observe(mainAtc);
          if (footer) {
            /* The theme editor swaps the footer section node when it is
               edited, so follow the replacement instead of a detached node */
            var footerObs = new IntersectionObserver(function (entries) {
              entries.forEach(function (entry) {
                if (!entry.target.isConnected) {
                  footerObs.unobserve(entry.target);
                  var fresh = document.querySelector('.sn-section-footer') || document.querySelector('.sn-footer') || document.querySelector('footer');
                  if (fresh) footerObs.observe(fresh);
                  return;
                }
                footerOn = entry.isIntersecting;
              });
              syncBar();
            }, { threshold: 0 });
            footerObs.observe(footer);
          }
        }
        window.addEventListener('resize', debounce(function () {
          if (bar.classList.contains('is-visible')) root.style.setProperty('--sn-bar-h', bar.offsetHeight + 'px');
        }, 100));
      }

      /* ---- Option sheet ----------------------------------------------- */
      var lastFocus = null;
      var selectBtns = Array.prototype.slice.call(root.querySelectorAll('[data-sn-sheet-open]'));
      var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

      /* Everything that is not the sheet (or an ancestor of it) goes inert while it is open */
      function setInert(on) {
        if (!sheet || !('inert' in document.body)) return;
        var nodes = Array.prototype.slice.call(document.body.children);
        var el = root;
        while (el && el !== document.body) {
          nodes = nodes.concat(Array.prototype.slice.call(el.children));
          el = el.parentNode;
        }
        nodes.forEach(function (node) {
          if (node === sheet || node.contains(sheet)) return;
          if (node.tagName === 'SCRIPT' || node.tagName === 'STYLE' || node.tagName === 'TEMPLATE') return;
          if (on) node.setAttribute('inert', ''); else node.removeAttribute('inert');
        });
      }

      function sheetFocusables() {
        return Array.prototype.filter.call(sheet.querySelectorAll(FOCUSABLE), function (el) {
          return el.offsetParent !== null;
        });
      }

      function openSheet(index) {
        if (!sheet) return;
        lastFocus = document.activeElement;
        sheet.classList.add('is-open');
        sheet.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('sn-sheet-open');
        setInert(true);
        selectBtns.forEach(function (b) {
          b.classList.toggle('is-open', String(b.getAttribute('data-sn-sheet-open')) === String(index));
        });
        Array.prototype.forEach.call(sheet.querySelectorAll('[data-sn-opt]'), function (g) {
          g.classList.toggle('is-focus', String(g.getAttribute('data-sn-opt')) === String(index));
        });
        var group = sheet.querySelector('[data-sn-opt="' + index + '"]') || sheet.querySelector('[data-sn-opt]');
        window.setTimeout(function () {
          if (group && group.scrollIntoView) group.scrollIntoView({ block: 'nearest' });
          var first = group ? group.querySelector('[data-sn-opt-btn]') : null;
          if (first) first.focus();
        }, 60);
      }

      function closeSheet() {
        if (!sheet || !sheet.classList.contains('is-open')) return;
        sheet.classList.remove('is-open');
        sheet.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('sn-sheet-open');
        setInert(false);
        selectBtns.forEach(function (b) { b.classList.remove('is-open'); });
        if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
      }

      selectBtns.forEach(function (b) {
        b.addEventListener('click', function () {
          var index = parseInt(b.getAttribute('data-sn-sheet-open'), 10);
          openSheet(isNaN(index) ? 0 : index);
        });
      });
      Array.prototype.forEach.call(root.querySelectorAll('[data-sn-sheet-close]'), function (b) {
        b.addEventListener('click', closeSheet);
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') closeSheet();
      });
      if (sheet) {
        sheet.addEventListener('keydown', function (e) {
          if (e.key !== 'Tab' || !sheet.classList.contains('is-open')) return;
          var items = sheetFocusables();
          if (!items.length) return;
          var first = items[0];
          var last = items[items.length - 1];
          if (e.shiftKey && (document.activeElement === first || !sheet.contains(document.activeElement))) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && (document.activeElement === last || !sheet.contains(document.activeElement))) {
            e.preventDefault();
            first.focus();
          }
        });
      }

      /* ---- Accordions: one open at a time --------------------------- */
      var accs = Array.prototype.slice.call(root.querySelectorAll('[data-sn-accordions] details'));
      accs.forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (!d.open) return;
          accs.forEach(function (o) { if (o !== d && o.open) o.open = false; });
        });
      });

      updateVariant(true);
      showMediaFor(priceVariant(), false);
    });
  }

  /* =====================================================================
     SECTION 10 — "More For You"
     The section renders empty on first paint; fetch it again through the
     Section Rendering API with product_id and swap the markup in.
     ===================================================================== */

  /* =====================================================================
     SECTION 5b — Product cards
     Colour swatches swap the card photo to that colour's image and refresh
     which sizes can be bought; the size boxes (shown on hover) are quick
     adds. Variant data comes from the JSON block in the card.
     ===================================================================== */

  function initProductCards() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-card]'), function (card) {
      if (card.dataset.snCardBound) return;
      card.dataset.snCardBound = 'true';

      var data = readJson(card.querySelector('[data-sn-card-data]'));
      if (!data || !data.v || !data.v.length) return;
      var ci = data.c;
      var si = data.s;
      var variants = data.v;
      var img = card.querySelector('.sn-card__media .sn-card__img:not(.sn-card__img--hover)');
      var hover = card.querySelector('.sn-card__img--hover');
      var orig = img ? { src: img.getAttribute('src'), srcset: img.getAttribute('srcset'), sizes: img.getAttribute('sizes') } : null;
      var links = Array.prototype.slice.call(card.querySelectorAll('[data-sn-card-link]'));
      var swatches = Array.prototype.slice.call(card.querySelectorAll('[data-sn-card-color]'));
      var sizes = Array.prototype.slice.call(card.querySelectorAll('[data-sn-card-size]'));
      var priceEl = card.querySelector('[data-sn-card-price]');
      var baseUrl = card.getAttribute('data-sn-card-url') || '#';
      var addUrl = card.getAttribute('data-sn-card-add') || '/cart/add.js';
      var color = null;
      swatches.forEach(function (b) {
        if (b.getAttribute('aria-pressed') === 'true') color = b.getAttribute('data-sn-card-color');
      });

      function matching(c, s) {
        return variants.filter(function (v) {
          if (ci > -1 && c != null && v.o[ci] !== c) return false;
          if (si > -1 && s != null && v.o[si] !== s) return false;
          return true;
        });
      }

      function pickFrom(list) {
        for (var i = 0; i < list.length; i++) if (list[i].a) return list[i];
        return list[0] || null;
      }

      function variantUrl(v) {
        if (!v) return baseUrl;
        return baseUrl + (baseUrl.indexOf('?') > -1 ? '&' : '?') + 'variant=' + v.id;
      }

      function apply(c, swapImage) {
        color = c;
        swatches.forEach(function (b) {
          b.setAttribute('aria-pressed', b.getAttribute('data-sn-card-color') === c ? 'true' : 'false');
        });
        var pick = pickFrom(matching(c, null));
        if (swapImage && img && orig) {
          if (pick && pick.img && pick.img !== orig.src) {
            img.removeAttribute('srcset');
            img.removeAttribute('sizes');
            img.src = pick.img;
            if (hover) hover.hidden = true;
          } else {
            if (orig.srcset) img.setAttribute('srcset', orig.srcset); else img.removeAttribute('srcset');
            if (orig.sizes) img.setAttribute('sizes', orig.sizes); else img.removeAttribute('sizes');
            img.src = orig.src;
            if (hover) hover.hidden = false;
          }
        }
        sizes.forEach(function (b) {
          var ok = matching(c, b.getAttribute('data-sn-card-size')).some(function (v) { return v.a; });
          b.disabled = !ok;
        });
        var href = variantUrl(pick);
        links.forEach(function (a) { a.setAttribute('href', href); });
        if (pick && priceEl && data.f) writeMoney(priceEl, pick.p, data.f);
      }

      swatches.forEach(function (b) {
        b.addEventListener('click', function (e) {
          e.preventDefault();
          apply(b.getAttribute('data-sn-card-color'), true);
        });
      });

      sizes.forEach(function (b) {
        b.addEventListener('click', function (e) {
          e.preventDefault();
          if (b.classList.contains('is-busy')) return;
          var v = pickFrom(matching(color, b.getAttribute('data-sn-card-size')).filter(function (x) { return x.a; }));
          if (!v) return;
          b.classList.add('is-busy');
          fetch(addUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ id: v.id, quantity: 1 })
          })
            .then(function (r) {
              if (!r.ok) throw new Error('add failed');
              return r.json();
            })
            .then(function () {
              bumpCartCount(1);
              b.classList.add('is-added');
              window.setTimeout(function () { b.classList.remove('is-added'); }, 1500);
              document.dispatchEvent(new CustomEvent('sn:cart:added', { detail: { variantId: v.id, quantity: 1 } }));
            })
            .catch(function () {
              window.location.href = variantUrl(v);
            })
            .then(function () {
              b.classList.remove('is-busy');
            });
        });
      });

      if (color != null || si > -1) apply(color, false);
    });
  }


  /* =====================================================================
     SECTION 12 — Cart drawer
     Slides in from the right. Contents come from /cart.js, so anything that
     adds to the cart anywhere on the site (it fires sn:cart:added) refreshes
     and opens this without a page reload.
     ===================================================================== */

  function initCart() {
    var root = document.querySelector('[data-sn-cart]');
    if (!root || root.dataset.snBound) return;
    root.dataset.snBound = 'true';

    var body = root.querySelector('[data-sn-cart-body]');
    var foot = root.querySelector('[data-sn-cart-foot]');
    var countEl = root.querySelector('[data-sn-cart-count]');
    var totalEl = root.querySelector('[data-sn-cart-total]');
    var cfg = readJson(root.querySelector('[data-sn-cart-config]')) || {};
    var labels = cfg.labels || {};
    var money = cfg.moneyFormat || '${{amount}}';
    var lastFocus = null;
    var loaded = false;

    /* The header is a fixed, z-indexed wrapper, so the drawer has to sit at the
       top level to cover the lookbook and notify popups too. */
    Array.prototype.forEach.call(document.querySelectorAll('body > [data-sn-cart]'), function (n) {
      if (n !== root) n.parentNode.removeChild(n);
    });
    if (root.parentElement !== document.body) document.body.appendChild(root);

    function esc(str) {
      return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    function sized(url, w) {
      if (!url) return '';
      return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + w;
    }

    /* "Black, 28" — the option values, skipping Shopify's placeholder */
    function variantLine(item) {
      var parts = [];
      var opts = item.options_with_values || [];
      for (var i = 0; i < opts.length; i++) {
        var v = opts[i] && opts[i].value;
        if (v && v !== 'Default Title') parts.push(v);
      }
      if (!parts.length && item.variant_title && item.variant_title !== 'Default Title') {
        parts = String(item.variant_title).split(' / ');
      }
      return parts.join(', ');
    }

    function itemHtml(item, i) {
      var line = variantLine(item);
      var img = item.image || (item.featured_image && item.featured_image.url) || '';
      return (
        '<li class="sn-cart__item" data-sn-cart-item="' + esc(item.key) + '" style="--sn-cart-delay: ' + (i * 55) + 'ms">' +
          (img
            ? '<a class="sn-cart__thumb" href="' + esc(item.url) + '" tabindex="-1" aria-hidden="true"><img src="' + esc(sized(img, 260)) + '" alt="" loading="lazy"></a>'
            : '<span class="sn-cart__thumb"></span>') +
          '<div class="sn-cart__info">' +
            '<a class="sn-cart__name" href="' + esc(item.url) + '">' + esc(item.product_title || item.title) + '</a>' +
            (line ? '<p class="sn-cart__variant">' + esc(line) + '</p>' : '') +
            '<p class="sn-cart__price">' + esc(formatMoney(item.price, money)) + '</p>' +
            '<div class="sn-cart__qty">' +
              '<button class="sn-cart__qty-btn" type="button" data-sn-cart-minus aria-label="' + esc(labels.less || 'Decrease quantity') + '"' + (item.quantity < 2 ? ' disabled' : '') + '>' +
                '<svg class="sn-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14"/></svg>' +
              '</button>' +
              '<span class="sn-cart__qty-value" data-sn-cart-qty>' + item.quantity + '</span>' +
              '<button class="sn-cart__qty-btn" type="button" data-sn-cart-plus aria-label="' + esc(labels.more || 'Increase quantity') + '">' +
                '<svg class="sn-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14"/><path d="M5 12h14"/></svg>' +
              '</button>' +
            '</div>' +
          '</div>' +
          '<button class="sn-cart__remove" type="button" data-sn-cart-remove aria-label="' + esc(labels.remove || 'Remove') + '">' +
            '<svg class="sn-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12"/><path d="M18 6L6 18"/></svg>' +
          '</button>' +
        '</li>'
      );
    }

    function emptyHtml() {
      return (
        '<div class="sn-cart__empty">' +
          '<p class="sn-cart__empty-title">' + esc(labels.empty || 'Your cart is empty') + '</p>' +
          '<a class="sn-cart__shop" href="' + esc(cfg.shopUrl || '/collections/all') + '">' + esc(labels.shop || 'Shop All') + '</a>' +
        '</div>'
      );
    }

    /* The total swipes up from zero: the number is tweened and the whole
       value rides a small vertical offset that eases out as it settles, so
       it reads as digits rolling rather than flickering. */
    function countTo(el, cents) {
      if (!el) return;
      var target = parseInt(cents, 10) || 0;
      if (reduceMotion.matches || !window.requestAnimationFrame) {
        el.textContent = formatMoney(target, money);
        return;
      }
      var DUR = 760;
      var start = null;
      var last = 0;
      el.textContent = formatMoney(0, money);
      el.style.setProperty('--sn-roll', '0em');
      el.style.setProperty('--sn-roll-blur', '0px');
      function frame(now) {
        if (start == null) start = now;
        var t = Math.min(1, (now - start) / DUR);
        var eased = 1 - Math.pow(1 - t, 3);
        var value = Math.round(target * eased);
        el.textContent = formatMoney(value, money);
        var step = Math.abs(value - last) / Math.max(1, target);
        last = value;
        var roll = Math.min(0.3, step * 9);
        el.style.setProperty('--sn-roll', (-roll).toFixed(3) + 'em');
        el.style.setProperty('--sn-roll-blur', (roll * 4).toFixed(2) + 'px');
        if (t < 1) {
          window.requestAnimationFrame(frame);
        } else {
          el.style.setProperty('--sn-roll', '0em');
          el.style.setProperty('--sn-roll-blur', '0px');
        }
      }
      window.requestAnimationFrame(frame);
    }

    function render(cart, animate) {
      var items = (cart && cart.items) || [];
      var n = (cart && cart.item_count) || 0;
      if (countEl) countEl.textContent = n ? n : '';
      if (!items.length) {
        body.classList.remove('is-entering');
        body.innerHTML = emptyHtml();
        if (foot) foot.hidden = true;
        return;
      }
      body.classList.remove('is-entering');
      body.innerHTML = '<ul class="sn-cart__list">' + items.map(itemHtml).join('') + '</ul>';
      if (animate) {
        void body.offsetWidth;
        body.classList.add('is-entering');
      }
      if (foot) foot.hidden = false;
      if (animate) countTo(totalEl, cart.total_price);
      else if (totalEl) totalEl.textContent = formatMoney(cart.total_price, money);
    }

    function load(animate) {
      return window
        .fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          loaded = true;
          render(cart, animate);
          return cart;
        })
        ['catch'](function () {
          if (!loaded) render(null);
        });
    }

    /* Quantity edits go straight to /cart/change.js and re-render */
    function change(key, quantity) {
      root.classList.add('is-busy');
      return window
        .fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: quantity })
        })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          render(cart);
          setCartCount(cart.item_count);
        })
        ['catch'](function () {})
        .then(function () {
          root.classList.remove('is-busy');
        });
    }

    function open() {
      lastFocus = document.activeElement;
      root.classList.add('is-open');
      root.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('sn-cart-open');
      load(true);
      window.setTimeout(function () {
        var close = root.querySelector('[data-sn-cart-close].sn-cart__close');
        if (close) close.focus();
      }, 60);
    }

    function close() {
      if (!root.classList.contains('is-open')) return;
      root.classList.remove('is-open');
      root.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('sn-cart-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-sn-cart-close]'), function (b) {
      b.addEventListener('click', close);
    });

    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-cart-open]'), function (b) {
      b.addEventListener('click', function (e) {
        e.preventDefault();
        open();
      });
    });

    body.addEventListener('click', function (e) {
      var row = e.target.closest ? e.target.closest('[data-sn-cart-item]') : null;
      if (!row) return;
      var key = row.getAttribute('data-sn-cart-item');
      var qty = parseInt(row.querySelector('[data-sn-cart-qty]').textContent, 10) || 1;
      if (e.target.closest('[data-sn-cart-remove]')) { e.preventDefault(); change(key, 0); }
      else if (e.target.closest('[data-sn-cart-plus]')) { e.preventDefault(); change(key, qty + 1); }
      else if (e.target.closest('[data-sn-cart-minus]')) { e.preventDefault(); if (qty > 1) change(key, qty - 1); }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' || e.key === 'Esc') close();
    });

    /* Anything that adds to the cart pops the drawer so it is obvious */
    document.addEventListener('sn:cart:added', function () {
      open();
    });

    window.SNTheme.openCart = open;
    window.SNTheme.closeCart = close;
    window.SNTheme.renderCart = render;
    window.SNTheme.cartItemHtml = itemHtml;
  }


  /* =====================================================================
     SECTION 13 — Decode-in text
     A fifth of the characters land as symbols and cycle through the set
     before settling on the real letter. Each one runs for its own
     0.2-0.7s: flat out at ~15 fps for the first half, easing down to
     ~1 fps by the end.
     ===================================================================== */

  var DECODE_GLYPHS = ['★', '✦', '🞺', '✸', '#', '$', '%', '*', '&', '€', '¥', '₩', '£'];
  var DECODE_FAST = 66;
  var DECODE_SLOW = 1000;

  function scrambleIn(el, opts) {
    opts = opts || {};
    var glyphs = opts.glyphs || DECODE_GLYPHS;
    var ratio = opts.ratio == null ? 0.2 : opts.ratio;
    var minDur = opts.min == null ? 200 : opts.min;
    var spread = opts.spread == null ? 500 : opts.spread;
    var order = opts.order || 0;
    var fast = opts.fast || DECODE_FAST;
    var slow = opts.slow || DECODE_SLOW;

    var real = el.textContent;
    if (!real || !real.trim()) return 0;
    if (reduceMotion.matches || !window.requestAnimationFrame) return 0;

    var chars = real.split('');
    var slots = [];
    for (var i = 0; i < chars.length; i++) {
      if (chars[i].trim()) slots.push(i);
    }
    if (!slots.length) return 0;

    var shuffled = slots.slice();
    for (var j = shuffled.length - 1; j > 0; j--) {
      var k = Math.floor(Math.random() * (j + 1));
      var tmp = shuffled[j];
      shuffled[j] = shuffled[k];
      shuffled[k] = tmp;
    }

    function glyph(avoid) {
      var g = glyphs[Math.floor(Math.random() * glyphs.length)];
      for (var tries = 0; g === avoid && tries < 4; tries++) {
        g = glyphs[Math.floor(Math.random() * glyphs.length)];
      }
      return g;
    }

    var take = Math.max(1, Math.round(shuffled.length * ratio));
    var picked = {};
    var active = [];
    for (var n = 0; n < take; n++) picked[shuffled[n]] = true;

    var frag = document.createDocumentFragment();
    for (var c = 0; c < chars.length; c++) {
      if (!picked[c]) {
        frag.appendChild(document.createTextNode(chars[c]));
        continue;
      }
      var slot = document.createElement('span');
      slot.className = 'sn-decode__slot is-live';
      var letter = document.createElement('span');
      letter.className = 'sn-decode__real';
      letter.textContent = chars[c];
      var ghost = document.createElement('span');
      ghost.className = 'sn-decode__ghost';
      ghost.textContent = glyph(active.length ? active[active.length - 1].ghost.textContent : null);
      slot.appendChild(letter);
      slot.appendChild(ghost);
      frag.appendChild(slot);
      var pos = slots.length > 1 ? slots.indexOf(c) / (slots.length - 1) : 0;
      var dur = minDur + spread * (order * pos + (1 - order) * Math.random());
      active.push({ slot: slot, ghost: ghost, dur: dur, next: 0 });
    }
    el.textContent = '';
    el.appendChild(frag);

    function settle(item) {
      item.done = true;
      item.slot.classList.remove('is-live');
      if (item.ghost.parentNode) item.ghost.parentNode.removeChild(item.ghost);
    }

    var start = null;

    function frame(now) {
      if (start == null) start = now;
      var elapsed = now - start;
      var live = 0;
      for (var a = 0; a < active.length; a++) {
        var item = active[a];
        if (item.done) continue;
        if (elapsed >= item.dur) {
          settle(item);
          continue;
        }
        live++;
        if (elapsed >= item.next) {
          item.ghost.textContent = glyph(item.ghost.textContent);
          var p = elapsed / item.dur;
          var ramp = p < 0.5 ? 0 : (p - 0.5) / 0.5;
          item.next = elapsed + fast + (slow - fast) * ramp * ramp;
        }
      }
      if (live) window.requestAnimationFrame(frame);
      else el.textContent = real;
    }

    window.requestAnimationFrame(frame);

    var longest = 0;
    for (var d = 0; d < active.length; d++) longest = Math.max(longest, active[d].dur);
    window.setTimeout(function () {
      el.textContent = real;
    }, longest + 500);
    return longest;
  }

  function initDecode() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-decode]'), function (el) {
      if (el.dataset.snDecoded) return;
      el.dataset.snDecoded = 'true';
      scrambleIn(el);
    });
  }

  /* =====================================================================
     SECTION 14 — Loading screen
     Only rendered on the landing page, and only the first time in a
     session (sn-header.liquid drops it inline if it has already run).
     ===================================================================== */

  var LOADER_GLYPHS = ['★', '✦', '✸', '✶', '✺', '$', '$', '★', 'N', 'A', 'R', 'E', 'C', 'S', 'T', 'U'];

  function whenDisplayFontReady(cap) {
    return new Promise(function (resolve) {
      window.setTimeout(resolve, cap);
      if (document.fonts && document.fonts.load) {
        document.fonts.load('800 1em "GC Avalance"').then(resolve, resolve);
      } else {
        resolve();
      }
    });
  }

  function initLoader() {
    var el = document.querySelector('[data-sn-loader]');
    if (!el || el.dataset.snBound) return;
    el.dataset.snBound = 'true';

    var word = el.querySelector('[data-sn-loader-word]');
    var MIN_ON_SCREEN = 1000;
    var CAP = 8000;
    var HOLD = 500;
    var opened = Date.now();
    var finished = false;
    var loaded = false;
    var decoded = !word;

    function finish() {
      if (finished) return;
      finished = true;
      try { window.sessionStorage.setItem('sn-loader-seen', '1'); } catch (err) {}
      window.setTimeout(function () {
        el.classList.add('is-done');
        window.setTimeout(function () {
          if (el.parentNode) el.parentNode.removeChild(el);
        }, 800);
      }, Math.max(0, MIN_ON_SCREEN - (Date.now() - opened)));
    }

    function maybeFinish() {
      if (loaded && decoded) finish();
    }

    if (word) {
      whenDisplayFontReady(1200).then(function () {
        if (finished) return;
        el.classList.add('is-ready');
        var total = scrambleIn(word, {
          glyphs: LOADER_GLYPHS,
          ratio: 1,
          min: 450,
          spread: 1150,
          order: 0.55,
          fast: 40,
          slow: 200
        });
        window.setTimeout(function () {
          decoded = true;
          maybeFinish();
        }, Math.max(600, total + HOLD));
      });
    }

    function onLoad() {
      loaded = true;
      maybeFinish();
    }

    if (document.readyState === 'complete') onLoad();
    else window.addEventListener('load', onLoad);
    window.setTimeout(finish, CAP);
  }

  /* =====================================================================
     SECTION 15 — Scroll reveal
     Cards further down a page fade up as they arrive. Only items that
     start below the fold are touched, so nothing can be left hidden if
     the observer never fires.
     ===================================================================== */

  var REVEAL_SELECTOR = '.sn-rail__track > .sn-card, .sn-rail__track > .sn-look, .sn-lookbook__track > .sn-look, .sn-banner__inner, .sn-rv-card';

  function initReveal() {
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;

    /* Everything arriving together is staggered in reading order, one every
       100ms, so the delay is relative to the batch rather than the page. */
    var io = new IntersectionObserver(
      function (entries) {
        var arriving = [];
        entries.forEach(function (entry) {
          if (entry.isIntersecting) arriving.push(entry);
        });
        arriving.sort(function (a, b) {
          return (
            a.boundingClientRect.top - b.boundingClientRect.top ||
            a.boundingClientRect.left - b.boundingClientRect.left
          );
        });
        arriving.forEach(function (entry, i) {
          entry.target.style.setProperty('--sn-reveal-delay', i * 100 + 'ms');
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    Array.prototype.forEach.call(document.querySelectorAll(REVEAL_SELECTOR), function (el) {
      if (el.dataset.snRevealBound) return;
      var box = el.getBoundingClientRect();
      /* Already on screen: leave it alone rather than hide then re-show */
      if (box.top < window.innerHeight * 0.92) return;
      el.dataset.snRevealBound = 'true';
      el.classList.add('sn-reveal');
      io.observe(el);
    });
  }

  /* =====================================================================
     SECTION 10 — Collection page sort
     The select sits in a GET form (works without JS); with JS a change
     reloads the page with sort_by set and the page number cleared.
     ===================================================================== */

  function initCollectionSort() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-sort-select]'), function (select) {
      if (select.dataset.snBound) return;
      select.dataset.snBound = 'true';
      select.addEventListener('change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      });
    });
  }

  function initRecommendations() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-recs]'), function (section) {
      if (section.dataset.snRecsBound) return;
      section.dataset.snRecsBound = 'true';
      var url = section.getAttribute('data-sn-recs-url');
      if (!url) return;
      fetch(url)
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-sn-recs]');
          if (fresh && fresh.querySelector('.sn-card')) {
            section.innerHTML = fresh.innerHTML;
            section.hidden = false;
            delete section.dataset.snBound;
            initProductRail();
    initProductCards();
          } else if (!section.querySelector('.sn-card')) {
            /* Shopify had nothing and the Liquid fallback is empty too */
            section.hidden = true;
          }
        })
        .catch(function () {
          if (!section.querySelector('.sn-card')) section.hidden = true;
        });
    });
  }

  /* =====================================================================
     SECTION 11 — Recently viewed
     Handles come from localStorage (written by initProductPage). Each one
     is fetched as a rendered card via sections/sn-rv-card.liquid.
     ===================================================================== */

  function initRecentlyViewed() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-recent]'), function (section) {
      if (section.dataset.snRecentBound) return;
      section.dataset.snRecentBound = 'true';
      var trackEl = section.querySelector('[data-sn-recent-track]');
      if (!trackEl) return;
      var exclude = section.getAttribute('data-sn-recent-exclude');
      var limit = parseInt(section.getAttribute('data-sn-recent-limit'), 10) || 8;
      var root = (section.getAttribute('data-sn-recent-root') || '/').replace(/\/$/, '');
      var source = section.getAttribute('data-sn-recent-source') === 'wishlist' ? readWishlistHandles() : readRecent();
      var handles = source.filter(function (h) { return h !== exclude; }).slice(0, limit);
      if (!handles.length) return;

      var jobs = handles.map(function (h) {
        if (window.SN_MOCK_CARDS && window.SN_MOCK_CARDS[h]) return Promise.resolve(window.SN_MOCK_CARDS[h]);
        return fetch(root + '/products/' + encodeURIComponent(h) + '?section_id=sn-rv-card')
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var card = doc.querySelector('.sn-card');
            return card ? card.outerHTML : '';
          })
          .catch(function () { return ''; });
      });

      Promise.all(jobs).then(function (cards) {
        var html = cards.filter(Boolean).join('');
        if (!html) return;
        trackEl.innerHTML = html;
        section.hidden = false;
        delete section.dataset.snBound;
        initProductRail();
        initProductCards();
      });
    });
  }

  function initAccount() {
    var root = document.querySelector('[data-sn-acct]');
    if (!root || root.dataset.snBound) return;
    root.dataset.snBound = 'true';

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-sn-acct-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-sn-acct-panel]'));
    if (!panels.length) return;

    var names = panels.map(function (p) { return p.getAttribute('data-sn-acct-panel'); });
    var cfg = readJson(root.querySelector('[data-sn-acct-config]')) || {};
    var labels = cfg.labels || {};
    var money = cfg.moneyFormat || '${{amount}}';
    var base = String(cfg.root || '/').replace(/\/?$/, '/');
    var current = null;
    var favsState = 'idle';

    var favGrid = root.querySelector('[data-sn-acct-favs]');
    var favEmpty = root.querySelector('[data-sn-acct-favs-empty]');
    var favLoading = root.querySelector('[data-sn-acct-favs-loading]');
    var cartBox = root.querySelector('[data-sn-acct-cart]');
    var cartItems = root.querySelector('[data-sn-acct-cart-items]');
    var cartList = root.querySelector('[data-sn-acct-cart-list]');
    var cartSummary = root.querySelector('[data-sn-acct-cart-summary]');
    var cartEmpty = root.querySelector('[data-sn-acct-cart-empty]');
    var cartTotal = root.querySelector('[data-sn-acct-cart-total]');

    function setCount(name, n) {
      Array.prototype.forEach.call(root.querySelectorAll('[data-sn-acct-count="' + name + '"]'), function (el) {
        el.textContent = n > 0 ? String(n) : '';
        el.hidden = !(n > 0);
      });
    }

    function show(name, write) {
      if (names.indexOf(name) < 0) name = names[0];
      if (name === current) return;
      current = name;
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-sn-acct-tab') === name;
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'page');
        else t.removeAttribute('aria-current');
      });
      panels.forEach(function (p) {
        var on = p.getAttribute('data-sn-acct-panel') === name;
        p.hidden = !on;
        p.classList.remove('is-entering');
        if (on) {
          void p.offsetWidth;
          p.classList.add('is-entering');
        }
      });
      if (name === 'favourites') loadFavourites();
      if (name === 'cart') loadCart();
      if (write !== false && window.history && window.history.replaceState) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search + '#' + name);
      }
    }

    function resolveFavourites() {
      var ids = readWishlist();
      var map = readWishHandleMap();
      var missing = ids.filter(function (id) { return !map[id]; });
      if (!missing.length) return Promise.resolve(readWishlistHandles());
      return window
        .fetch(base + 'products.json?limit=250', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (data) {
          (data.products || []).forEach(function (p) {
            var id = String(p.id);
            if (missing.indexOf(id) > -1) map[id] = p.handle;
          });
          try { window.localStorage.setItem(WISH_HANDLES_KEY, JSON.stringify(map)); } catch (err) {}
          return readWishlistHandles();
        })
        ['catch'](function () { return readWishlistHandles(); });
    }

    function showFavourites(html) {
      if (favLoading) favLoading.hidden = true;
      if (html) {
        favGrid.innerHTML = html;
        favGrid.hidden = false;
        if (favEmpty) favEmpty.hidden = true;
        initProductRail();
        initProductCards();
      } else {
        favGrid.innerHTML = '';
        favGrid.hidden = true;
        if (favEmpty) favEmpty.hidden = false;
      }
      setCount('favourites', readWishlist().length);
    }

    function loadFavourites() {
      if (!favGrid || favsState !== 'idle') return;
      favsState = 'loading';
      resolveFavourites().then(function (handles) {
        if (!handles.length) {
          favsState = 'done';
          showFavourites('');
          return;
        }
        var jobs = handles.map(function (h) {
          if (window.SN_MOCK_CARDS && window.SN_MOCK_CARDS[h]) return Promise.resolve(window.SN_MOCK_CARDS[h]);
          return window
            .fetch(base + 'products/' + encodeURIComponent(h) + '?section_id=sn-rv-card', { credentials: 'same-origin' })
            .then(function (r) { return r.ok ? r.text() : ''; })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var card = doc.querySelector('.sn-card');
              return card ? card.outerHTML : '';
            })
            ['catch'](function () { return ''; });
        });
        return Promise.all(jobs).then(function (cards) {
          favsState = 'done';
          showFavourites(cards.filter(Boolean).join(''));
        });
      });
    }

    if (favGrid) {
      favGrid.addEventListener(
        'click',
        function (e) {
          var btn = e.target.closest ? e.target.closest('[data-sn-wish]') : null;
          if (!btn) return;
          window.setTimeout(function () {
            setCount('favourites', readWishlist().length);
            if (btn.getAttribute('aria-pressed') === 'true') return;
            var card = btn.closest('.sn-card');
            if (!card) return;
            card.classList.add('is-leaving');
            window.setTimeout(function () {
              if (card.parentNode) card.parentNode.removeChild(card);
              if (!favGrid.querySelector('.sn-card')) showFavourites('');
            }, 280);
          }, 0);
        },
        true
      );
    }

    function renderCartPanel(cart) {
      if (!cartBox) return cart;
      var items = (cart && cart.items) || [];
      setCount('cart', (cart && cart.item_count) || 0);
      var itemHtml = window.SNTheme && window.SNTheme.cartItemHtml;
      if (!items.length || !itemHtml) {
        cartList.innerHTML = '';
        cartItems.hidden = true;
        cartSummary.hidden = true;
        cartEmpty.hidden = false;
        return cart;
      }
      cartList.innerHTML = items.map(itemHtml).join('');
      cartItems.hidden = false;
      cartSummary.hidden = false;
      cartEmpty.hidden = true;
      if (cartTotal) cartTotal.textContent = formatMoney(cart.total_price, money);
      return cart;
    }

    function syncCart(cart) {
      renderCartPanel(cart);
      setCartCount(cart.item_count);
      if (window.SNTheme && window.SNTheme.renderCart) window.SNTheme.renderCart(cart);
      return cart;
    }

    function fetchCart() {
      return window
        .fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); });
    }

    function loadCart() {
      return fetchCart().then(renderCartPanel)['catch'](function () {});
    }

    function changeLine(key, quantity) {
      cartBox.classList.add('is-busy');
      return window
        .fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: quantity })
        })
        .then(function (r) { return r.json(); })
        .then(syncCart)
        ['catch'](function () {})
        .then(function () { cartBox.classList.remove('is-busy'); });
    }

    if (cartList) {
      cartList.addEventListener('click', function (e) {
        var row = e.target.closest ? e.target.closest('[data-sn-cart-item]') : null;
        if (!row) return;
        var key = row.getAttribute('data-sn-cart-item');
        var qty = parseInt(row.querySelector('[data-sn-cart-qty]').textContent, 10) || 1;
        if (e.target.closest('[data-sn-cart-remove]')) { e.preventDefault(); changeLine(key, 0); }
        else if (e.target.closest('[data-sn-cart-plus]')) { e.preventDefault(); changeLine(key, qty + 1); }
        else if (e.target.closest('[data-sn-cart-minus]')) { e.preventDefault(); if (qty > 1) changeLine(key, qty - 1); }
      });
    }

    function postItems(items) {
      return window.fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items })
      });
    }

    function addAgain(items) {
      return postItems(items).then(function (r) {
        if (r.ok) return;
        return items.reduce(function (chain, item) {
          return chain.then(function () {
            return postItems([item]).then(function () {}, function () {});
          });
        }, Promise.resolve());
      });
    }

    function buyAgain(btn) {
      var items;
      try { items = JSON.parse(btn.getAttribute('data-sn-buy-again')); } catch (err) { items = []; }
      if (!items.length) return;
      var label = btn.textContent;
      btn.classList.add('is-busy');
      btn.textContent = labels.adding || label;
      addAgain(items)
        .then(function () {
          btn.textContent = labels.added || label;
          return fetchCart().then(syncCart);
        })
        ['catch'](function () {})
        .then(function () {
          show('cart');
          window.setTimeout(function () {
            btn.classList.remove('is-busy');
            btn.textContent = label;
          }, 1600);
        });
    }

    root.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var go = e.target.closest('[data-sn-acct-go]');
      if (go) {
        e.preventDefault();
        show(go.getAttribute('data-sn-acct-go'));
        return;
      }
      var tab = e.target.closest('[data-sn-acct-tab]');
      if (tab) {
        e.preventDefault();
        show(tab.getAttribute('data-sn-acct-tab'));
        return;
      }
      var again = e.target.closest('[data-sn-buy-again]');
      if (again && !again.classList.contains('is-busy')) {
        e.preventDefault();
        buyAgain(again);
      }
    });

    document.addEventListener('sn:cart:added', function () {
      loadCart();
    });

    window.addEventListener('hashchange', function () {
      show(window.location.hash.replace('#', ''), false);
    });

    var start = window.location.hash.replace('#', '');
    if (names.indexOf(start) < 0 && /[?&]customer_posted=true/.test(window.location.search)) start = 'profile';
    show(names.indexOf(start) > -1 ? start : names[0], false);
    setCount('favourites', readWishlist().length);
    loadCart();
  }

  function initAccountPopups() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sn-account]'), function (host) {
      if (host.dataset.snBound) return;
      host.dataset.snBound = 'true';
      var login = host.getAttribute('data-sn-login-url') || '/customer_authentication/login';
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      host.addEventListener('click', function () {
        if (!window.customElements || !window.customElements.get('shopify-account')) {
          window.location.href = login;
        }
      });

      host.addEventListener('close', function () {
        window
          .fetch(root + 'pages/profile?section_id=sn-profile', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (html) {
            if (html.indexOf('data-sn-acct-panel') > -1) window.location.reload();
          })
          ['catch'](function () {});
      });
    });
  }

  var PARALLAX_SELECTOR = '[data-sn-parallax], .sn-hero__media, .sn-feature__media, .sn-banner__media, .sn-about__media';
  var PARALLAX_SHIFT = 1.5;
  var PARALLAX_EASE = 0.06;
  var parallax = { bound: false, layers: [], visible: [], tx: 0, ty: 0, x: 0, y: 0, raf: 0, io: null };

  function parallaxEnabled() {
    return !reduceMotion.matches && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  function paintParallax() {
    var x = (-parallax.x * PARALLAX_SHIFT).toFixed(3) + '%';
    var y = (-parallax.y * PARALLAX_SHIFT).toFixed(3) + '%';
    parallax.visible.forEach(function (el) {
      el.style.setProperty('--sn-px-x', x);
      el.style.setProperty('--sn-px-y', y);
    });
  }

  function stepParallax() {
    parallax.x += (parallax.tx - parallax.x) * PARALLAX_EASE;
    parallax.y += (parallax.ty - parallax.y) * PARALLAX_EASE;
    if (Math.abs(parallax.tx - parallax.x) < 0.001 && Math.abs(parallax.ty - parallax.y) < 0.001) {
      parallax.x = parallax.tx;
      parallax.y = parallax.ty;
      parallax.raf = 0;
    } else {
      parallax.raf = window.requestAnimationFrame(stepParallax);
    }
    paintParallax();
  }

  function kickParallax() {
    if (!parallax.raf) parallax.raf = window.requestAnimationFrame(stepParallax);
  }

  function aimParallax(x, y) {
    parallax.tx = x;
    parallax.ty = y;
    kickParallax();
  }

  function initParallax() {
    if (!parallax.bound) {
      parallax.bound = true;
      parallax.io = 'IntersectionObserver' in window
        ? new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
              var i = parallax.visible.indexOf(entry.target);
              if (entry.isIntersecting && i < 0) parallax.visible.push(entry.target);
              if (!entry.isIntersecting && i > -1) parallax.visible.splice(i, 1);
            });
            paintParallax();
          })
        : null;

      window.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse' || !parallaxEnabled()) return;
        var w = window.innerWidth || 1;
        var h = window.innerHeight || 1;
        aimParallax(
          Math.max(-1, Math.min(1, (e.clientX / w) * 2 - 1)),
          Math.max(-1, Math.min(1, (e.clientY / h) * 2 - 1))
        );
      }, { passive: true });

      document.documentElement.addEventListener('mouseleave', function () {
        aimParallax(0, 0);
      });

      reduceMotion.addEventListener('change', function () {
        if (reduceMotion.matches) {
          parallax.tx = parallax.ty = parallax.x = parallax.y = 0;
          paintParallax();
        }
        initParallax();
      });
    }

    parallax.layers = parallax.layers.filter(function (el) {
      if (document.contains(el)) return true;
      if (parallax.io) parallax.io.unobserve(el);
      var i = parallax.visible.indexOf(el);
      if (i > -1) parallax.visible.splice(i, 1);
      return false;
    });

    var on = parallaxEnabled();
    Array.prototype.forEach.call(document.querySelectorAll(PARALLAX_SELECTOR), function (el) {
      el.classList.toggle('sn-px', on);
      if (parallax.layers.indexOf(el) > -1) return;
      parallax.layers.push(el);
      if (parallax.io) parallax.io.observe(el);
      else parallax.visible.push(el);
    });
    paintParallax();
  }

  /* =====================================================================
     Boot
     ===================================================================== */

  window.SNTheme = window.SNTheme || {};

  function boot() {
    initLoader();
    initHeaderScroll();
    initLogoLayer();
    initSearchOverlay();
    initMobileMenu();
    initLocaleDrawer();
    initTicker();
    initNewsletter();
    initProductRail();
    initProductCards();
    initLookbook();
    initProductPage();
    initRecommendations();
    initCollectionSort();
    initCart();
    initDecode();
    initRecentlyViewed();
    initReveal();
    initParallax();
    initAccount();
    initAccountPopups();
    window.SNTheme.booted = true;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(initTicker);
  }

  window.addEventListener(
    'resize',
    debounce(function () {
      initHeaderScroll();
      initTicker();
    }, 150)
  );

  reduceMotion.addEventListener('change', initTicker);

  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:reorder', boot);

  window.SNTheme.initHeaderScroll = initHeaderScroll;
  window.SNTheme.initLogoLayer = initLogoLayer;
  window.SNTheme.initSearchOverlay = initSearchOverlay;
  window.SNTheme.initTicker = initTicker;
  window.SNTheme.initReveal = initReveal;
  window.SNTheme.initParallax = initParallax;
  window.SNTheme.initDecode = initDecode;
  window.SNTheme.initNewsletter = initNewsletter;
  window.SNTheme.initProductRail = initProductRail;
  window.SNTheme.initMobileMenu = initMobileMenu;
  window.SNTheme.initLocaleDrawer = initLocaleDrawer;
  window.SNTheme.initLookbook = initLookbook;
})();
